import * as z$1 from "zod/v4";
//#region src/schemas/http/profile-space-history.ts
const ISO_DATE_TIME_SCHEMA = z$1.iso.datetime({ offset: true });
const GATEWAY_HOST_MAX_LENGTH = 255;
const PROFILE_SPACE_HISTORY_MAX_LIMIT = 500;
const NON_NEGATIVE_INT_RE = /^\d+$/;
const GATEWAY_HOST_PATH_CHARS_RE = /[/?#]/;
const trimmedNonEmptyStringSchema = z$1.string().trim().min(1);
const nullableTrimmedStringSchema = z$1.union([trimmedNonEmptyStringSchema, z$1.null()]);
const nullableNonNegativeIntSchema = z$1.union([z$1.number().int().min(0), z$1.null()]);
const nullableIsoDateTimeSchema = z$1.union([ISO_DATE_TIME_SCHEMA, z$1.null()]);
const profileSpaceHistoryGatewayHostSchema = z$1.string().transform((value, ctx) => {
	const normalized = value.trim().toLowerCase();
	if (normalized.length === 0) {
		ctx.addIssue({
			code: "custom",
			message: "gatewayHost is required"
		});
		return z$1.NEVER;
	}
	if (normalized.includes("://")) try {
		const url = new URL(normalized);
		if (url.pathname !== "/" || url.search.length > 0 || url.hash.length > 0) {
			ctx.addIssue({
				code: "custom",
				message: "gatewayHost must not include path/query/hash"
			});
			return z$1.NEVER;
		}
		if (url.host.length === 0) {
			ctx.addIssue({
				code: "custom",
				message: "gatewayHost is required"
			});
			return z$1.NEVER;
		}
		return url.host.toLowerCase();
	} catch {
		ctx.addIssue({
			code: "custom",
			message: "gatewayHost must be a valid host"
		});
		return z$1.NEVER;
	}
	if (GATEWAY_HOST_PATH_CHARS_RE.test(normalized)) {
		ctx.addIssue({
			code: "custom",
			message: "gatewayHost must not include path/query/hash"
		});
		return z$1.NEVER;
	}
	return normalized;
}).pipe(z$1.string().max(GATEWAY_HOST_MAX_LENGTH));
const profileSpaceHistoryLimitSchema = z$1.preprocess((value) => {
	if (typeof value === "number") return value;
	if (typeof value !== "string") return value;
	const normalized = value.trim();
	if (!NON_NEGATIVE_INT_RE.test(normalized)) return NaN;
	return Number.parseInt(normalized, 10);
}, z$1.number().int().min(1).max(PROFILE_SPACE_HISTORY_MAX_LIMIT)).optional();
const profileSpaceHistoryPinnedFilterSchema = z$1.union([z$1.boolean(), z$1.enum(["true", "false"])]).optional().transform((value) => {
	if (value === void 0 || typeof value === "boolean") return value;
	return value === "true";
});
const profileSpaceHistoryStatusSchema = z$1.enum([
	"active",
	"removed",
	"inaccessible"
]);
const profileSpaceCreatorKindSchema = z$1.enum([
	"human",
	"agent",
	"system"
]);
const profileSpaceHistoryStatusListSchema = z$1.union([profileSpaceHistoryStatusSchema, z$1.array(profileSpaceHistoryStatusSchema)]).optional().transform((value) => {
	if (value === void 0) return;
	return Array.isArray(value) ? value : [value];
});
const profileSpaceHistoryRecordSchema = z$1.strictObject({
	profileId: trimmedNonEmptyStringSchema,
	gatewayHost: profileSpaceHistoryGatewayHostSchema,
	spaceId: trimmedNonEmptyStringSchema,
	spaceName: nullableTrimmedStringSchema,
	creatorProfileId: nullableTrimmedStringSchema,
	creatorKind: z$1.union([profileSpaceCreatorKindSchema, z$1.null()]),
	spaceCreatedAt: nullableIsoDateTimeSchema,
	spaceUpdatedAt: nullableIsoDateTimeSchema,
	lastJoinedAt: ISO_DATE_TIME_SCHEMA,
	lastSeenTs: nullableNonNegativeIntSchema,
	pinnedAt: nullableIsoDateTimeSchema,
	status: profileSpaceHistoryStatusSchema,
	createdAt: ISO_DATE_TIME_SCHEMA,
	updatedAt: ISO_DATE_TIME_SCHEMA
});
const listProfileSpaceHistoryQuerySchema = z$1.strictObject({
	gatewayHost: profileSpaceHistoryGatewayHostSchema,
	status: profileSpaceHistoryStatusListSchema,
	limit: profileSpaceHistoryLimitSchema,
	pinned: profileSpaceHistoryPinnedFilterSchema
});
const listProfileSpaceHistoryResponseSchema = z$1.strictObject({ items: z$1.array(profileSpaceHistoryRecordSchema) });
const profileSpaceHistorySpaceIndexOverlaySchema = z$1.strictObject({
	repairAttempted: z$1.boolean(),
	status: z$1.enum([
		"applied",
		"degraded",
		"pending"
	]),
	updated: z$1.boolean()
});
const upsertProfileSpaceHistoryBodySchema = z$1.strictObject({
	gatewayHost: profileSpaceHistoryGatewayHostSchema,
	spaceName: nullableTrimmedStringSchema.optional(),
	creatorProfileId: nullableTrimmedStringSchema.optional(),
	creatorKind: z$1.union([profileSpaceCreatorKindSchema, z$1.null()]).optional(),
	spaceCreatedAt: nullableIsoDateTimeSchema.optional(),
	spaceUpdatedAt: nullableIsoDateTimeSchema.optional(),
	lastJoinedAt: ISO_DATE_TIME_SCHEMA.optional(),
	lastSeenTs: nullableNonNegativeIntSchema.optional(),
	pinnedAt: nullableIsoDateTimeSchema.optional(),
	status: profileSpaceHistoryStatusSchema.optional()
});
const upsertProfileSpaceHistoryResponseSchema = z$1.strictObject({
	item: profileSpaceHistoryRecordSchema,
	spaceIndexOverlay: profileSpaceHistorySpaceIndexOverlaySchema
});
const patchProfileSpaceHistoryStatusBodySchema = z$1.strictObject({
	gatewayHost: profileSpaceHistoryGatewayHostSchema,
	status: profileSpaceHistoryStatusSchema,
	reason: z$1.string().trim().min(1).max(500).optional()
});
const patchProfileSpaceHistoryStatusResponseSchema = z$1.strictObject({
	profileId: trimmedNonEmptyStringSchema,
	gatewayHost: profileSpaceHistoryGatewayHostSchema,
	spaceId: trimmedNonEmptyStringSchema,
	status: profileSpaceHistoryStatusSchema,
	updatedAt: ISO_DATE_TIME_SCHEMA
});
//#endregion
export { listProfileSpaceHistoryQuerySchema, listProfileSpaceHistoryResponseSchema, patchProfileSpaceHistoryStatusBodySchema, patchProfileSpaceHistoryStatusResponseSchema, profileSpaceCreatorKindSchema, profileSpaceHistoryGatewayHostSchema, profileSpaceHistoryRecordSchema, profileSpaceHistorySpaceIndexOverlaySchema, profileSpaceHistoryStatusSchema, upsertProfileSpaceHistoryBodySchema, upsertProfileSpaceHistoryResponseSchema };

//# sourceMappingURL=profile-space-history.js.map